import React from 'react'
import { Info, MapPin } from 'lucide-react'
import Tooltip from '../../ui/Tooltip'
import { ExposureZone, PublishedBroadcast } from '../../../services/broadcastService'
import { formatAreaSeconds, formatMoney, valueOf } from '../broadcastValuation'

interface Props {
  broadcast: PublishedBroadcast
  zones: ExposureZone[]
  rate: number | null
  currency: string
}

/**
 * Where on the broadcast the value came from.
 *
 * DELIBERATELY A SLOT, NOT A TABLE. A drawn venue — "your logo was HERE" on a
 * ballpark or a court outline — beats a ranked list badly for a sponsor, and we
 * intend to build one. A panel whose first implementation happens to be a table
 * absorbs that drawing later with nothing else moving; a table-shaped section
 * with a diagram bolted on afterwards means re-laying out the page. The cost of
 * choosing now is zero and the cost of retrofitting is not, so the seam is here
 * from the start.
 *
 * SPORT-SCOPED BY THE SERVER. A customer has one sport, so the zones arriving
 * here are already that sport's — baseball sees backstop and outfield fence,
 * volleyball sees net band and court apron. Nothing cross-sport is normalised
 * or compared, by decision.
 *
 * KNOWN GAP: zones are not yet grouped into sellable inventory versus
 * incidental appearance. A flat ranked list puts `crowd` and `unknown` beside
 * the outfield fence as though a rightsholder could sell them. That grouping
 * belongs on the zone record rather than here — see backlog #38.
 */
const ExposurePlacement: React.FC<Props> = ({ broadcast, zones, rate, currency }) => {
  const rows = [...zones]
    .map((z) => ({ ...z, area: Number(z.total_area_seconds) || 0 }))
    .filter((z) => z.area > 0)
    .sort((a, b) => b.area - a.area)

  if (rows.length === 0) return null

  const total = rows.reduce((s, z) => s + z.area, 0)

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-1">
        <MapPin className="h-3.5 w-3.5 text-gray-400" />
        <h3 className="text-sm font-semibold text-gray-800">Where the value came from</h3>
        <Tooltip
          maxWidth={270}
          content={
            'Area-seconds by placement. Placements are specific to ' +
            `${broadcast.sport_name ?? 'this sport'}, so they describe the actual ` +
            'inventory rather than generic positions.'
          }
        >
          <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
        </Tooltip>
      </div>
      <p className="text-[11px] text-gray-500 mb-4">
        {rows.length} placement{rows.length === 1 ? '' : 's'} carried brand exposure.
      </p>

      {/* DIAGRAM SLOT — a drawn venue renders above the table once we have one,
          and the table stays as its legend and its fallback. */}

      <div className="space-y-2">
        {rows.map((z) => {
          const share = total ? (z.area / total) * 100 : 0
          const { value } = valueOf(z.area, rate)
          return (
            <div key={z.zone_code} className="flex items-center gap-3 text-xs">
              <span className="w-36 flex-shrink-0 truncate text-gray-700 font-medium">
                {z.zone_label}
              </span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
                     style={{ width: `${Math.max(share, 0.8)}%` }} />
              </div>
              <span className="w-12 text-right tabular-nums text-gray-500">
                {share.toFixed(1)}%
              </span>
              <span className="w-16 text-right tabular-nums text-gray-800">
                {formatAreaSeconds(z.area)}
              </span>
              {rate !== null && (
                <span className="w-20 text-right tabular-nums font-medium text-gray-900">
                  {formatMoney(value, currency)}
                </span>
              )}
              <span className="w-16 text-right text-gray-400">
                {z.brands} brand{z.brands === 1 ? '' : 's'}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ExposurePlacement
